/** @format */
import { combineLatest, defer, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { AngularFirestore } from '@angular/fire/firestore';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DbService {
  constructor(public afs: AngularFirestore) {}

  /**
   * firebase collection 데이터 불러오기
   * @param path 경로
   * @param query
   * @returns
   */
  collection$(path: string, query?) {
    return this.afs
      .collection(path, query)
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data: any = a.payload.doc.data();
            const id = a.payload.doc.id;
            return { id: id, ...data };
          });
        })
      );
  }

  /**
   * 파이어베이스 콜렉션 데이터 불러오기 (+ delay 시간 주기)
   * @param path 경로
   * @param query
   * @returns
   */
  collection2$(path: string, query?) {
    return this.afs
      .collection(path, query)
      .snapshotChanges()
      .pipe(
        debounceTime(200),
        map((actions) => {
          return actions.map((a) => {
            const data: any = a.payload.doc.data();
            const id = a.payload.doc.id;
            return { id: id, ...data };
          });
        })
      );
  }

  /**
   * firebase document 데이터 불러오기
   * @param path 경로
   * @returns
   */
  doc$(path: string) {
    return this.afs
      .doc(path)
      .snapshotChanges()
      .pipe(
        map((doc) => {
          const data: any = doc.payload.data();
          const id = doc.payload.id;
          return { id: id, ...data };
        })
      );
  }

  /**
   * firebase에 데이터 업데이트
   * @param path 경로
   * @param data 업데이트할 데이터
   * @returns
   */
  updateAt(path: string, data: Object): Promise<any> {
    const segments = path.split('/').filter((v) => v);
    if (segments.length % 2) {
      return this.afs.collection(path).add(data);
    } else {
      return this.afs.doc(path).set(data, { merge: true });
    }
  }

  /**
   * firebase 데이터 삭제
   * @param path 경로
   * @returns
   */
  delete(path: string) {
    return this.afs.doc(path).delete();
  }
}

/**
 * let posts = [{uid : 'test234234', content : '블라블라'},{uid : 'test1234', content : '블라블라'}] =>
 * [{ uid : {id :'test234234', age :23}, content : '블라블라'},{uid : {id :'test1234', age :29}, content : '블라블라'}]
 *  userId만 가지고있는 post들의 Array를 가져와서 post마다 각각의 userId(글쓴이)의 정보(user document)를 불러와서 엮는 경우
 * @param afs  afs
 * @param field   field
 * @param collection   collection
 */

export const leftJoinDocument = (afs: AngularFirestore, field, collection) => {
  return (source) =>
    defer(() => {
      // Operator state
      let collectionData;
      const cache = new Map();

      return source.pipe(
        switchMap((data) => {
          cache.clear();
          collectionData = data as any[]; // array

          const reads$ = [];
          let i = 0;
          for (const doc of collectionData) {
            if (!doc[field] || cache.get(doc[field])) {
              // {} 에 userId filed가 없는경우
              continue;
            }

            reads$.push(
              afs.collection(collection).doc(doc[field]).valueChanges()
            );
            cache.set(doc[field], i);
            i++;
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map((joins) => {
          return collectionData.map((v) => {
            const joinIdx = cache.get(v[field]); // 고유ID
            return { ...v, [field]: joins[joinIdx] || null };
          });
        })
      );
    });
};

/**
 *  한개의 post안에 userId 아이디를 가지고 있고 userId(글쓴이)의 정보(user document)를 불러와서 엮는 경우
 *  userId(field)가 default값이 되어야합니다.
 * @param afs
 * @param field
 * @param collection
 */
export const docJoin = (
  afs: AngularFirestore,
  field: string,
  collection: string
) => {
  return (source) =>
    defer(() => {
      let parent;
      return source.pipe(
        switchMap((data) => {
          parent = data;

          const id = data[field];

          if (!id) {
            return of({});
          } else {
            return afs
              .doc(`${collection}/${id}`)
              .snapshotChanges()
              .pipe(
                map((doc) => {
                  return doc.payload.data();
                })
              );
          }
        }),
        map((fieldData) => {
          delete parent[field];
          return { [field]: fieldData, ...parent };
        })
      );
    });
};
